// File: frontend/src/pages/reports/ReportsDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import { exportToPDF } from '../../utils/pdfExport';
import api from '../../utils/axios';

const ReportsDashboard = () => {
  const { school } = useSchool();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/v1/reports/summary');
        if (res.data?.success) {
          setSummary(res.data.data);
        }
      } catch (err) {
        console.error('Error fetching report summary:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  /**
   * Handle Quick PDF Export for Summary Report
   */
  const handleExportSummaryPDF = () => {
    if (!summary) return;
    const headers = ['Metric Description', 'Value'];
    const data = [
      ['Total Active Students', summary.total_students],
      ['Total Employed Staff', summary.total_staff],
      ['Fee Collected This Month', `Rs. ${summary.fee_collected_month.toLocaleString()}`],
      ['Today Attendance Rate', `${summary.today_attendance_percentage}%`]
    ];
    exportToPDF('Executive Summary Report', headers, data, school?.name);
  };

  const reportCards = [
    {
      id: 'students',
      title: 'Students Report',
      description: 'Demographics, active/inactive ratios, gender breakdown & class distributions.',
      icon: (
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      path: '/reports/students'
    },
    {
      id: 'attendance',
      title: 'Attendance Report',
      description: 'Monthly attendance statistics, present/absent percentages by class & section.',
      icon: (
        <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      path: '/reports/attendance'
    },
    {
      id: 'fees',
      title: 'Fee Collection Report',
      description: 'Monthly collection ledgers, outstanding balances & fee defaulter tracking.',
      icon: (
        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bg: 'bg-purple-50',
      border: 'border-purple-100',
      path: '/reports/fees'
    },
    {
      id: 'exams',
      title: 'Examination Report',
      description: 'Term exam performance, class pass/fail counts, subject averages & top performers.',
      icon: (
        <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      path: '/reports/exams'
    },
    {
      id: 'staff',
      title: 'Staff & HR Report',
      description: 'Departmental staff allocation, active status, and monthly payroll budget outlay.',
      icon: (
        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      bg: 'bg-indigo-50',
      border: 'border-indigo-100',
      path: '/reports/staff'
    },
    {
      id: 'summary',
      title: 'Summary Executive Report',
      description: 'Comprehensive single-page overview of all institutional key performance indicators.',
      icon: (
        <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      bg: 'bg-slate-100',
      border: 'border-slate-200',
      isSummary: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 p-1 flex items-center justify-center shadow-sm">
              {school?.logo ? (
                <img src={school.logo} alt="Logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="font-extrabold text-primary text-sm">SMS</span>
              )}
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Reports & Analytics Hub</h1>
              <p className="text-xs text-slate-500">Institutional reporting and export center</p>
            </div>
          </div>

          <Link
            to="/dashboard/admin"
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Quick KPI Overview Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Total Students</span>
            <span className="text-2xl font-black text-slate-900">{loading ? '...' : summary?.total_students || 0}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Total Staff</span>
            <span className="text-2xl font-black text-slate-900">{loading ? '...' : summary?.total_staff || 0}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Fees Collected (Month)</span>
            <span className="text-2xl font-black text-emerald-600">Rs. {loading ? '...' : parseFloat(summary?.fee_collected_month || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}</span>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-slate-400 text-xs font-bold uppercase block mb-1">Today Attendance</span>
            <span className="text-2xl font-black text-blue-600">{loading ? '...' : (summary?.today_attendance_percentage || 100)}%</span>
          </div>
        </div>

        {/* 6 Report Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportCards.map((card) => (
            <div key={card.id} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition flex flex-col justify-between space-y-6">
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl ${card.bg} ${card.border} border flex items-center justify-center`}>
                    {card.icon}
                  </div>
                  <h3 className="font-bold text-slate-900 text-base">{card.title}</h3>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed font-medium">
                  {card.description}
                </p>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                {card.isSummary ? (
                  <button
                    onClick={handleExportSummaryPDF}
                    className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs shadow-sm transition flex items-center justify-center gap-2"
                  >
                    📄 Quick Export Summary PDF
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => navigate(card.path)}
                      className="flex-1 py-2 bg-primary hover:bg-blue-600 text-white font-bold rounded-xl text-xs shadow-sm transition"
                    >
                      View Report
                    </button>
                    <button
                      onClick={() => navigate(card.path)}
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition"
                    >
                      PDF
                    </button>
                  </>
                )}
              </div>

            </div>
          ))}
        </div>

      </main>
    </div>
  );
};

export default ReportsDashboard;
